import { useMyCompletion } from "@/components/engagements/my-completion";
import { useApps } from "@/hooks/apps";
import { CheckIcon, CornerDownRightIcon, CornerLeftUpIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip } from "@/components/badge/chip";
import { RowCard } from "@/components/list/row-card";
import { Section } from "@/components/section";
import { IssueTypeChip } from "@/components/prds/issue-type-chip";
import { CopyShortId } from "@/components/prds/copy-short-id";
import OwnerCell from "@/components/prds/owner-cell";
import { prdDetailPath } from "@/lib/paths";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { mutateRequest, patchListItem, putFetcherKeyed, swrKey, writeAtom } from "@/lib/swr";
import { cn } from "@/lib/utils";
import type { Prd } from "@/types/domain";

// Parent / children relation (WED-143): a plain link between issues — no
// lifecycle coupling (closing the parent never cascades; the close gate
// ignores it). The server validates self-reference, cycles and visibility.

// Header line above the title: "↑ CCP-42 主需求". Hidden without a parent.
export const ParentLine = ({ prd }: { prd: Prd }) => {
  const parent = prd.parent;
  if (!parent) return null;
  return (
    <Link
      to={prdDetailPath(parent.id, parent.engagementId ?? undefined)}
      className="mb-1 flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
    >
      <CornerLeftUpIcon className="h-3.5 w-3.5 shrink-0" />
      {parent.shortId && (
        <span className="font-mono text-xs tabular-nums">{parent.shortId}</span>
      )}
      <span className="min-w-0 truncate">{parent.docName}</span>
    </Link>
  );
};

// Mounted by the header menu. Search visible PRDs (todos may also hang under
// a bug, WED-183) and pick a document, deduplicating its App rows; the server
// remains authoritative for cycles and allowed parent types.
export const ParentPickerDialog = ({
  prd,
  onOpenChange,
}: {
  prd: Prd;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Pick<Prd, "id" | "shortId" | "docName"> | null>(prd.parent ?? null);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const isTodo = prd.type === "todo";
  // 缺省 type 即 prd + bug 混排
  // keepPreviousData: typing keeps the last result list while the next query loads.
  const { data, isLoading, isValidating, error, mutate: reload } = useSWR<{ data: Prd[]; total: number }>(
    swrKey("/prds", { ...(isTodo ? {} : { type: "prd" }), q: query, pageSize: 50 }),
    { keepPreviousData: true }
  );
  const excluded = new Set([prd.id, ...(prd.children ?? []).map(child => child.id)]);
  const candidates = [...new Map((data?.data ?? [])
    .filter(candidate => !excluded.has(candidate.id))
    .map(candidate => [candidate.id, candidate])).values()];
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}#parent`,
    putFetcherKeyed<Prd>,
    {
      // The PUT echoes the PRD: write the detail back and patch list rows in
      // place; only the old / new parents' details (children lists) reload (WED-249).
      onSuccess: (saved) => {
        writeAtom(`/prds/${prd.id}`, saved);
        patchListItem("/prds", prd.id, { parentId: saved.parentId ?? null, parent: saved.parent ?? null });
        for (const parentId of new Set([prd.parentId, saved.parentId].filter(Boolean))) {
          mutateRequest(`/prds/${parentId}`);
        }
        onOpenChange(false);
      },
      onError: (e: unknown) => toastError(toast, "设置父项失败", e, "提交失败"),
    }
  );
  const save = (parentId: string | null) => trigger({ parentId }).catch(() => {});
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isTodo ? "选择父项" : "选择父需求"}</DialogTitle>
          <DialogDescription>搜索标题或 short ID，选择后显示在当前标题上方。</DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput placeholder="搜索标题或 short ID…" value={search} onValueChange={setSearch} disabled={isMutating} />
          <CommandList>
            {error ? (
              <div role="alert" className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                加载失败<Button variant="ghost" size="sm" onClick={() => reload()}>重试</Button>
              </div>
            ) : isLoading && !data ? (
              <div className="py-3 text-sm text-muted-foreground">搜索中…</div>
            ) : (
              <>
                {(isValidating || search.trim() !== query) && (
                  <div className="px-2 py-1 text-xs text-muted-foreground">搜索中…</div>
                )}
                <CommandEmpty>{isTodo ? "没有找到需求或 bug。" : "没有找到需求。"}</CommandEmpty>
                <CommandGroup>
                  {candidates.map(candidate => (
                    <CommandItem key={candidate.id} value={candidate.id} aria-label={[candidate.shortId, candidate.docName].filter(Boolean).join(" ")} disabled={isMutating} onSelect={() => setSelected(candidate)}>
                      {candidate.shortId && <span className="shrink-0 font-mono text-xs text-muted-foreground">{candidate.shortId}</span>}
                      <IssueTypeChip prd={candidate} />
                      <span className="min-w-0 flex-1 truncate">{candidate.docName}</span>
                      {selected?.id === candidate.id && <CheckIcon aria-hidden="true" className="h-4 w-4" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
        {selected && <p className="truncate text-sm text-muted-foreground">已选择：{selected.shortId} {selected.docName}</p>}
        <DialogFooter>
          {prd.parentId && !isTodo && (
            <Button variant="ghost" size="sm" disabled={isMutating} onClick={() => save(null)}>移除父需求</Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>取消</Button>
          <Button size="sm" disabled={isMutating || !selected || selected.id === prd.parentId} onClick={() => selected && save(selected.id)}>保存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 勾选只切换本人 Done / Open;其他参与人的进度和整个 App 关系保持原样。
// parentId: the checked state lives on the parent detail's children, so the
// hook patches and revalidates that cache (exact keys only since WED-249).
const TodoCheckbox = ({ todo, parentId }: { todo: Prd; parentId: string }) => {
  const { setMyStatus, isMutating } = useMyCompletion(todo.engagementId ?? "", parentId);
  const e = todo.engagements?.[0];
  const done = !!e?.myCompletedAt;
  return <Checkbox checked={done} disabled={isMutating || !e?.canComplete}
    aria-label={done ? "Undo my completion" : "Complete my todo"}
    onCheckedChange={(v) => setMyStatus(v === true ? "done" : "open")} />;
};

// Direct children grouped by kind. Sub PRDs sit below the title, above the
// separator; other children stay below the document. Todos retain personal completion controls.
const GROUPS: { type: NonNullable<Prd["type"]>; title: string }[] = [
  { type: "prd", title: "子需求" },
  { type: "bug", title: "Bug" },
  { type: "todo", title: "Todo" },
  { type: "doc", title: "Doc" },
];

export const PrdChildrenSection = ({
  prd,
  types,
  className = "mb-6",
}: {
  prd: Prd;
  types?: NonNullable<Prd["type"]>[];
  className?: string;
}) => {
  const navigate = useNavigate();
  const apps = useApps();
  const children = (prd.children ?? []).filter(
    (child) => !types || types.includes(child.type ?? "prd")
  );
  if (children.length === 0) return null;
  const groups = GROUPS.map((g) => ({
    ...g,
    rows: children.filter((c) => (c.type ?? "prd") === g.type),
  })).filter((g) => g.rows.length > 0);
  const content = (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.type} className="space-y-1">
          {groups.length > 1 && g.type !== "prd" && (
            <div className="text-xs text-muted-foreground">
              {g.title} · {g.rows.length}
            </div>
          )}
          {g.rows.map((c) =>
            c.type === "todo" ? (
              // onClick mode (not `to`): the checkbox is a button and can't
              // nest in an <a>; RowCard skips clicks on inner controls.
              <RowCard key={c.engagementId ?? c.id} onClick={() => navigate(prdDetailPath(c.id, c.engagementId))}>
                <TodoCheckbox todo={c} parentId={prd.id} />
                <CopyShortId shortId={c.shortId} />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    c.engagements?.[0]?.myCompletedAt &&
                      "text-muted-foreground line-through"
                  )}
                >
                  {c.docName}
                </span>
                <Chip>{apps.find(a => String(a.id) === c.appId)?.name ?? c.appId}</Chip>
                <OwnerCell prd={c} />
              </RowCard>
            ) : (
              <RowCard key={c.engagementId ?? c.id} to={prdDetailPath(c.id, c.engagementId)}>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {g.type === "prd" && (
                    <CornerDownRightIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <CopyShortId shortId={c.shortId} />
                  <IssueTypeChip prd={c} />
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      !!c.myCompletedAt && "text-muted-foreground line-through"
                    )}
                  >
                    {c.docName}
                  </span>
                </div>
                {g.type !== "prd" && (
                  <>
                    <Chip>{apps.find(a => String(a.id) === c.appId)?.name ?? c.appId}</Chip>
                    <OwnerCell prd={c} />
                  </>
                )}
              </RowCard>
            )
          )}
        </div>
      ))}
    </div>
  );
  if (groups.length === 1 && groups[0].type === "prd") {
    return <section aria-label="子需求" className={className}>{content}</section>;
  }
  return (
    <Section title={`${groups.length === 1 ? groups[0].title : "子项"} (${children.length})`} className={className}>
      {content}
    </Section>
  );
};
